/**
 * Modules Editor - TICKET ZKPVN1
 * 
 * Editor principal de módulos y actividades del curso.
 * Gestiona estado local del temario y lo sincroniza con el backend.
 */

import React, { useState, useEffect } from 'react';
import { Save, Loader, AlertCircle, Plus, CheckCircle, BookOpen } from 'lucide-react';
import ModuleCard from './ModuleCard';
import ModuleModal from './ModuleModal';
import { updateCourse } from '../../services/adminCoursesApi';

const ModulesEditor = ({ course, onCourseUpdate }) => {
  // Estado local del temario (editable)
  const [modules, setModules] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  // Modal de módulo
  const [moduleModalOpen, setModuleModalOpen] = useState(false);
  const [editingModule, setEditingModule] = useState(null);

  // Cargar modules desde course prop
  useEffect(() => {
    if (course && course.modules) {
      setModules([...course.modules].sort((a, b) => a.order - b.order));
    }
  }, [course]);

  // Generar slug desde texto
  const generateSlug = (text) => {
    return text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-');
  };

  // Crear módulo
  const handleCreateModule = (moduleData) => {
    const newModule = {
      moduleId: moduleData.moduleId || generateSlug(moduleData.title),
      title: moduleData.title,
      description: moduleData.description || '',
      order: modules.length > 0 ? Math.max(...modules.map(m => m.order)) + 1 : 1,
      activities: []
    };

    setModules([...modules, newModule]);
    setModuleModalOpen(false);
  };

  // Editar módulo
  const handleEditModule = (moduleData) => {
    setModules(modules.map(m => 
      m.moduleId === editingModule.moduleId
        ? { ...m, title: moduleData.title, description: moduleData.description }
        : m
    ));
    setModuleModalOpen(false);
    setEditingModule(null);
  };

  // Eliminar módulo
  const handleDeleteModule = (moduleId) => {
    if (window.confirm('¿Seguro que deseas eliminar este módulo? Se eliminarán todas sus actividades.')) {
      setModules(modules.filter(m => m.moduleId !== moduleId));
    }
  };

  // Reordenar módulo (subir)
  const handleMoveModuleUp = (moduleId) => {
    const index = modules.findIndex(m => m.moduleId === moduleId);
    if (index <= 0) return;

    const newModules = [...modules];
    const temp = newModules[index].order;
    newModules[index].order = newModules[index - 1].order;
    newModules[index - 1].order = temp;

    setModules(newModules.sort((a, b) => a.order - b.order));
  };

  // Reordenar módulo (bajar)
  const handleMoveModuleDown = (moduleId) => {
    const index = modules.findIndex(m => m.moduleId === moduleId);
    if (index >= modules.length - 1) return;

    const newModules = [...modules];
    const temp = newModules[index].order;
    newModules[index].order = newModules[index + 1].order;
    newModules[index + 1].order = temp;

    setModules(newModules.sort((a, b) => a.order - b.order));
  };

  // Actualizar actividades de un módulo
  const handleUpdateModuleActivities = (moduleId, activities) => {
    setModules(modules.map(m =>
      m.moduleId === moduleId ? { ...m, activities } : m
    ));
  };

  // Validar temario antes de guardar
  const validateModules = () => {
    const errors = [];

    modules.forEach(module => {
      if (!module.title || module.title.trim() === '') {
        errors.push(`Módulo ${module.moduleId}: el título es requerido`);
      }

      if (module.activities) {
        module.activities.forEach(activity => {
          if (!activity.title || activity.title.trim() === '') {
            errors.push(`Actividad ${activity.activityId}: el título es requerido`);
          }

          if (activity.type === 'resource' && activity.resource) {
            if (activity.resource.subtype === 'youtube') {
              if (!activity.resource.youtubeUrl || activity.resource.youtubeUrl.trim() === '') {
                errors.push(`Actividad ${activity.activityId}: youtubeUrl es requerido para videos de YouTube`);
              }
            }
          }

          if (activity.estimatedMinutes === undefined || activity.estimatedMinutes < 0) {
            errors.push(`Actividad ${activity.activityId}: estimatedMinutes debe ser >= 0`);
          }
        });
      }
    });

    return errors;
  };

  // Guardar temario completo al backend
  const handleSave = async () => {
    // Validar frontend
    const validationErrors = validateModules();
    if (validationErrors.length > 0) {
      setSaveError(validationErrors.join('\n'));
      return;
    }

    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      // Construir payload completo (metadata + modules)
      const updates = {
        title: course.title,
        shortDescription: course.shortDescription,
        estimatedHours: course.estimatedHours,
        skills: course.skills,
        modules: modules
      };

      const updatedCourse = await updateCourse(course.courseId, updates);
      
      // Notificar al padre
      if (onCourseUpdate) {
        onCourseUpdate(updatedCourse);
      }

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Error guardando temario:', err);
      setSaveError(err.message || 'No se pudo guardar el temario');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Success Message */}
      {saveSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          <p className="text-green-700 font-medium">Temario guardado exitosamente</p>
        </div>
      )}

      {/* Error Message */}
      {saveError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-red-900 mb-1">Error de validación</h3>
              <pre className="text-red-700 text-sm whitespace-pre-wrap">{saveError}</pre>
            </div>
          </div>
        </div>
      )}

      {/* Header con botón agregar */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Módulos del curso</h3>
          <p className="text-sm text-gray-600">Crea y organiza los módulos y actividades</p>
        </div>
        <button
          onClick={() => {
            setEditingModule(null);
            setModuleModalOpen(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Agregar módulo
        </button>
      </div>

      {/* Lista de módulos */}
      {modules.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Este curso no tiene módulos
          </h3>
          <p className="text-gray-600 mb-4">
            Agrega el primer módulo para comenzar a construir el temario
          </p>
          <button
            onClick={() => {
              setEditingModule(null);
              setModuleModalOpen(true);
            }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Crear primer módulo
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {modules.map((module, index) => (
            <ModuleCard
              key={module.moduleId}
              module={module}
              isFirst={index === 0}
              isLast={index === modules.length - 1}
              onEdit={() => {
                setEditingModule(module);
                setModuleModalOpen(true);
              }}
              onDelete={() => handleDeleteModule(module.moduleId)}
              onMoveUp={() => handleMoveModuleUp(module.moduleId)}
              onMoveDown={() => handleMoveModuleDown(module.moduleId)}
              onUpdateActivities={(activities) => handleUpdateModuleActivities(module.moduleId, activities)}
            />
          ))}
        </div>
      )}

      {/* Botón guardar sticky */}
      {modules.length > 0 && (
        <div className="sticky bottom-0 bg-white border-t border-gray-200 py-4 -mx-6 px-6">
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 font-medium"
          >
            {saving ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                Guardando temario...
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                Guardar temario
              </>
            )}
          </button>
        </div>
      )}

      {/* Modal de módulo */}
      {moduleModalOpen && (
        <ModuleModal
          module={editingModule}
          existingIds={modules.map(m => m.moduleId)}
          onSave={editingModule ? handleEditModule : handleCreateModule}
          onClose={() => {
            setModuleModalOpen(false);
            setEditingModule(null);
          }}
        />
      )}
    </div>
  );
};

export default ModulesEditor;
